import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import { onPlatformNotificationReceived, updateUserCategory } from '../../../Socket/socket';
import { 
    FaBell, 
    FaBullhorn, 
    FaLaptopCode, 
    FaUsersCog, 
    FaRegLightbulb, 
    FaChartLine, 
    FaExclamationTriangle,
    FaSync,
    FaCheckCircle,
    FaCircle
} from 'react-icons/fa';

import { 
    COLORS, 
    PanelContent, 
    PanelTitle,
    NotificationList,
    NotificationItem
} from './styles';
import styled from 'styled-components';

// Icon mapping for notification types
const typeIcons = {
    feature: <FaLaptopCode />,
    tip: <FaBullhorn />,
    update: <FaSync />,
    feedback: <FaRegLightbulb />,
    announcement: <FaBullhorn />,
    alert: <FaExclamationTriangle />
};

// Styled components for notifications
const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
`;

const UnreadBadge = styled.span`
    background: ${COLORS.primary};
    color: white;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: 600;
`;

const MarkAllButton = styled.button`
    background: transparent;
    border: 1px solid ${COLORS.border};
    color: ${COLORS.muted};
    font-size: 10px;
    padding: 4px 8px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
        background: ${COLORS.bgHover};
        color: ${COLORS.light};
        border-color: ${COLORS.borderLight};
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

/* Filter bar. Kept on one wrapping row so the panel stays compact in the
   dashboard column; the selects fall to a second line on narrow screens
   rather than forcing the panel to scroll sideways. */
const FilterRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
`;

const FilterSelect = styled.select`
    background: transparent;
    border: 1px solid ${COLORS.border};
    color: ${COLORS.muted};
    font-size: 10px;
    padding: 4px 6px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover { border-color: ${COLORS.primary}; color: ${COLORS.primary}; }
    &:focus { outline: none; border-color: ${COLORS.primary}; }

    /* Native <option> ignores a transparent/rgba parent background, so give it
       an explicitly solid one or the list renders white-on-white in this theme. */
    option { background: ${COLORS.bgSecondary}; color: ${COLORS.light}; }
`;

const FilterCount = styled.span`
    font-size: 10px;
    color: ${COLORS.muted};
    margin-left: auto;
`;

const ClearFilters = styled.button`
    background: transparent;
    border: none;
    color: ${COLORS.primary};
    font-size: 10px;
    cursor: pointer;
    padding: 4px 2px;
    text-decoration: underline;

    &:hover { opacity: 0.8; }
`;

/* Time windows offered by the range filter. `days: null` means no limit. */
const TIME_RANGES = [
    { value: 'all', label: 'All time', days: null },
    { value: '7d', label: 'Last 7 days', days: 7 },
    { value: '30d', label: 'Last month', days: 30 },
    { value: '90d', label: 'Last 3 months', days: 90 },
    { value: '365d', label: 'Last year', days: 365 },
];


const UnreadDot = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 8px;
    height: 8px;
    background: ${COLORS.primary};
    border-radius: 50%;
`;

const NotificationItemWrapper = styled.div`
    position: relative;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: ${COLORS.muted};
    text-align: center;
    
    svg {
        font-size: 32px;
        margin-bottom: 12px;
        opacity: 0.5;
    }
    
    p {
        font-size: 13px;
        margin: 0;
    }
`;

const LoadingState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: ${COLORS.muted};
    
    svg {
        animation: spin 1s linear infinite;
        margin-right: 8px;
    }
    
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
`;

/**
 * Format date to relative time
 */
const formatRelativeTime = (date) => {
    if (!date) return '';
    
    const now = new Date();
    const notificationDate = new Date(date);
    const diffMs = now - notificationDate;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
    return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
};

/**
 * PlatformNotifications Component
 * 
 * Displays platform notifications and updates from the server
 */
const PlatformNotifications = () => {
    const { user } = useAuth0();
    const navigate = useNavigate();
    const authenticatedAxios = useAuthenticatedAxios();
    
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expertId, setExpertId] = useState(null);
    const [expertName, setExpertName] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const [markingRead, setMarkingRead] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');   // all | unread | read
    const [rangeFilter, setRangeFilter] = useState('all');     // see TIME_RANGES

    // Fetch expert profile to get expertId
    const fetchExpertProfile = useCallback(async () => {
        try {
            console.log('[PlatformNotifications] Fetching profile...');
            const response = await authenticatedAxios.get('/profile');
            console.log('[PlatformNotifications] Profile response:', response.data);
            const profile = response.data?.profile || response.data;
            console.log('[PlatformNotifications] Extracted profile:', profile);
            console.log('[PlatformNotifications] Profile keys:', profile ? Object.keys(profile) : 'null');
            
            // Try multiple possible ID fields
            const id = profile?._id || profile?.id || profile?.expertId || profile?.userId;
            console.log('[PlatformNotifications] Trying ID fields - _id:', profile?._id, 'id:', profile?.id, 'expertId:', profile?.expertId);
            
            if (id) {
                console.log('[PlatformNotifications] Expert ID found:', id);
                setExpertId(id);
                setExpertName(profile.name || user?.name || 'Expert');
            } else {
                console.warn('[PlatformNotifications] No expert ID in profile. Full profile:', JSON.stringify(profile));
                setLoading(false);
            }
        } catch (err) {
            console.error('[PlatformNotifications] Error fetching expert profile:', err);
            setLoading(false);
        }
    }, [authenticatedAxios, user]);

    // Fetch notifications for the expert
    const fetchNotifications = useCallback(async () => {
        if (!expertId) {
            console.log('[PlatformNotifications] No expertId, skipping fetch');
            return;
        }
        
        try {
            setLoading(true);
            setError(null);
            
            console.log('[PlatformNotifications] Fetching notifications for expert:', expertId);
            const response = await authenticatedAxios.get(`/platform-notifications/expert/${expertId}`);
            console.log('[PlatformNotifications] Notifications response:', response.data);
            const data = response.data;
            
            if (data.success) {
                console.log('[PlatformNotifications] Found', data.notifications?.length || 0, 'notifications');
                setNotifications(data.notifications || []);
                setUnreadCount(data.unreadCount || 0);
            } else {
                console.warn('[PlatformNotifications] API returned success=false');
            }
        } catch (err) {
            console.error('[PlatformNotifications] Error fetching notifications:', err);
            setError('Failed to load notifications');
            // Don't show error to user, just show empty state
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    }, [authenticatedAxios, expertId]);

    // Mark a single notification as read
    const markAsRead = useCallback(async (notificationId) => {
        if (!expertId) return;
        
        try {
            await authenticatedAxios.post(`/platform-notifications/${notificationId}/read`, {
                expertId,
                expertName
            });
            
            // Update local state
            setNotifications(prev => prev.map(n => 
                n._id === notificationId ? { ...n, isRead: true } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Error marking notification as read:', err);
        }
    }, [authenticatedAxios, expertId, expertName]);

    // Mark all notifications as read
    const markAllAsRead = useCallback(async () => {
        if (!expertId || markingRead) return;
        
        try {
            setMarkingRead(true);
            await authenticatedAxios.post('/platform-notifications/read-all', {
                expertId,
                expertName
            });
            
            // Update local state
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Error marking all notifications as read:', err);
        } finally {
            setMarkingRead(false);
        }
    }, [authenticatedAxios, expertId, expertName, markingRead]);

    // Fetch profile on mount
    useEffect(() => {
        fetchExpertProfile();
    }, [fetchExpertProfile]);

    // Fetch notifications when expertId is available
    useEffect(() => {
        if (expertId) {
            fetchNotifications();
        }
    }, [expertId, fetchNotifications]);

    // Listen for real-time platform notifications via socket
    useEffect(() => {
        const cleanup = onPlatformNotificationReceived((data) => {
            if (data.notification) {
                // Add new notification to the top of the list
                // Limit to 100 notifications to prevent memory leaks
                setNotifications(prev => [data.notification, ...prev].slice(0, 100));
                setUnreadCount(prev => prev + 1);
            }
        });

        return () => {
            if (cleanup) cleanup();
        };
    }, []);

    // Update user category on socket for targeted notifications
    useEffect(() => {
        const updateCategory = async () => {
            try {
                const response = await authenticatedAxios.get('/profile');
                const profile = response.data?.profile || response.data;
                if (profile?.userCategories) {
                    updateUserCategory(profile.userCategories);
                }
            } catch (err) {
                // Silently fail - category update is not critical
            }
        };

        if (expertId) {
            updateCategory();
        }
    }, [authenticatedAxios, expertId]);

    // Handle notification click
    const handleNotificationClick = (notification) => {
        if (!notification.isRead) {
            markAsRead(notification._id);
        }
        // Deep-link to the relevant in-app section (e.g. IRCC forms settings)
        if (notification.actionUrl) {
            navigate(notification.actionUrl);
        }
    };

    // Filtering is client-side: the feed is already capped and scoped to this
    // adviser, so switching a filter is instant and costs no request.
    const rangeDays = (TIME_RANGES.find(r => r.value === rangeFilter) || {}).days;
    const rangeCutoff = rangeDays ? Date.now() - rangeDays * 24 * 60 * 60 * 1000 : null;

    const visibleNotifications = notifications.filter(n => {
        if (statusFilter === 'unread' && n.isRead) return false;
        if (statusFilter === 'read' && !n.isRead) return false;
        if (rangeCutoff) {
            // A notification with no sentAt cannot be placed in time. Keep it
            // rather than hide it — silently dropping one is worse than showing
            // it outside its window.
            const sent = n.sentAt ? new Date(n.sentAt).getTime() : null;
            if (sent && sent < rangeCutoff) return false;
        }
        return true;
    });

    const filtersActive = statusFilter !== 'all' || rangeFilter !== 'all';

    return (
        <PanelContent>
            <HeaderRow>
                <PanelTitle style={{ marginBottom: 0 }}>
                    <FaBell /> Platform Notifications
                    {unreadCount > 0 && (
                        <UnreadBadge>{unreadCount} new</UnreadBadge>
                    )}
                </PanelTitle>
                {unreadCount > 0 && (
                    <MarkAllButton 
                        onClick={markAllAsRead} 
                        disabled={markingRead}
                    >
                        {markingRead ? 'Marking...' : 'Mark all read'}
                    </MarkAllButton>
                )}
            </HeaderRow>

            {notifications.length > 0 && (
                <FilterRow>
                    <FilterSelect
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        aria-label="Filter by read status"
                    >
                        <option value="all">All</option>
                        <option value="unread">Unread</option>
                        <option value="read">Read</option>
                    </FilterSelect>

                    <FilterSelect
                        value={rangeFilter}
                        onChange={e => setRangeFilter(e.target.value)}
                        aria-label="Filter by time range"
                    >
                        {TIME_RANGES.map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                    </FilterSelect>

                    {filtersActive && (
                        <ClearFilters
                            onClick={() => { setStatusFilter('all'); setRangeFilter('all'); }}
                        >
                            Clear
                        </ClearFilters>
                    )}

                    <FilterCount>
                        {visibleNotifications.length} of {notifications.length}
                    </FilterCount>
                </FilterRow>
            )}

            {loading ? (
                <LoadingState>
                    <FaSync />
                    Loading notifications...
                </LoadingState>
            ) : notifications.length === 0 ? (
                <EmptyState>
                    <FaCheckCircle />
                    <p>You're all caught up!</p>
                    <p style={{ fontSize: '11px', marginTop: '4px' }}>No new notifications</p>
                </EmptyState>
            ) : visibleNotifications.length === 0 ? (
                /* Distinct from the empty feed above: there ARE notifications,
                   they just fall outside the current filter. Saying "all caught
                   up" here would read as though they had disappeared. */
                <EmptyState>
                    <FaBell />
                    <p>Nothing matches this filter</p>
                    <p style={{ fontSize: '11px', marginTop: '4px' }}>
                        {notifications.length} notification{notifications.length === 1 ? '' : 's'} hidden
                    </p>
                    <ClearFilters
                        onClick={() => { setStatusFilter('all'); setRangeFilter('all'); }}
                        style={{ marginTop: '8px' }}
                    >
                        Clear filters
                    </ClearFilters>
                </EmptyState>
            ) : (
                <NotificationList>
                    {visibleNotifications.map(notification => (
                        <NotificationItemWrapper key={notification._id}>
                            <NotificationItem 
                                $type={notification.type}
                                onClick={() => handleNotificationClick(notification)}
                                style={{ 
                                    cursor: (!notification.isRead || notification.actionUrl) ? 'pointer' : 'default',
                                    opacity: notification.isRead ? 0.7 : 1 
                                }}
                            >
                                <div className="icon-container">
                                    {typeIcons[notification.type] || <FaBullhorn />}
                                </div>
                                <div className="content">
                                    <div className="title">{notification.title}</div>
                                    <div className="description">{notification.description}</div>
                                    <div className="date">{formatRelativeTime(notification.sentAt)}</div>
                                </div>
                                {!notification.isRead && <UnreadDot />}
                            </NotificationItem>
                        </NotificationItemWrapper>
                    ))}
                </NotificationList>
            )}
        </PanelContent>
    );
};

export default PlatformNotifications;
